import { useState } from 'react';
import { Link } from 'react-router-dom';

interface ReplyItem {
  id: string;
  post_id: string;
  content: string;
  created_at: string;
  post_title: string;
  post_category: string;
  post_author_nickname: string;
  post_author_avatar?: string;
}

interface ReplyHistoryTabProps {
  replies: ReplyItem[];
  loading: boolean;
  onDeleteReply: (replyId: string) => void;
}

function getCategoryStyle(category: string) {
  switch (category) {
    case 'つぶやき・モヤモヤ':
      return 'bg-rose-100 text-rose-700';
    case '教えて！ケアの知恵':
      return 'bg-amber-100 text-amber-700';
    case '保育園・社会参加':
      return 'bg-emerald-100 text-emerald-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

export default function ReplyHistoryTab({ replies, loading, onDeleteReply }: ReplyHistoryTabProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
        <div className="animate-spin w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full mx-auto mb-3"></div>
        <p className="text-gray-500 text-sm">読み込み中...</p>
      </div>
    );
  }

  if (replies.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 sm:p-12 text-center">
        <i className="ri-chat-off-line text-4xl sm:text-5xl text-gray-300 mb-3"></i>
        <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-1">回答履歴はありません</h3>
        <p className="text-gray-500 text-xs sm:text-sm">投稿に回答すると、ここに履歴が表示されます</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="text-base sm:text-lg font-bold text-gray-800">あなたの回答履歴</h3>
            <span className="text-xs sm:text-sm text-gray-500">{replies.length}件</span>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {replies.map(reply => (
            <div key={reply.id} className="p-4 sm:p-5 hover:bg-gray-50/50 transition-colors">
              <div className="flex items-start justify-between mb-2 gap-2">
                <div className="flex items-center space-x-2 flex-wrap gap-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryStyle(reply.post_category)}`}>
                    {reply.post_category}
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(reply.created_at).toLocaleString('ja-JP')}
                  </span>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
                  <Link
                    to={`/board/post/${reply.post_id}`}
                    className="px-2 sm:px-3 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    投稿を見る
                  </Link>
                  {confirmDeleteId === reply.id ? (
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => {
                          onDeleteReply(reply.id);
                          setConfirmDeleteId(null);
                        }}
                        className="px-2 py-1 bg-rose-500 text-white rounded-lg text-xs hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        削除
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="px-2 py-1 bg-gray-200 text-gray-600 rounded-lg text-xs hover:bg-gray-300 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        戻す
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(reply.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-rose-50 hover:text-rose-500 transition-colors cursor-pointer"
                      title="削除"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* 元投稿情報 */}
              <div className="flex items-center space-x-2 mb-2 overflow-hidden">
                {reply.post_author_avatar ? (
                  <img src={reply.post_author_avatar} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-smile-line text-gray-400 text-xs"></i>
                  </div>
                )}
                <span className="text-xs text-gray-500 flex-shrink-0">{reply.post_author_nickname}</span>
                <span className="text-xs text-gray-300 flex-shrink-0">|</span>
                <h4 className="font-bold text-gray-800 text-xs sm:text-sm truncate">{reply.post_title}</h4>
              </div>

              {/* 回答内容 */}
              <div
                className="bg-teal-50 border border-teal-100 rounded-xl p-2 sm:p-3 cursor-pointer"
                onClick={() => setExpandedId(expandedId === reply.id ? null : reply.id)}
              >
                <div className="flex items-start space-x-2">
                  <i className="ri-chat-check-line text-teal-500 text-xs sm:text-sm mt-0.5 flex-shrink-0"></i>
                  <p className={`text-xs sm:text-sm text-gray-700 ${expandedId === reply.id ? '' : 'line-clamp-2'}`}>
                    {reply.content}
                  </p>
                </div>
                {reply.content.length > 100 && (
                  <p className="text-xs text-teal-500 mt-1 text-right">
                    {expandedId === reply.id ? '閉じる' : '全文を表示'}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
